import React, { useState, useEffect, useRef } from "react";
import Sketch from "react-p5";
import p5Types from "p5";
import { P5State, Node, Edge, dummyState, dist } from "./p5-classes";

import { useStore } from "react-stores";
// import { StoreEvent, StoreEventType } from "react-stores";
import { globalStore, MallocState } from "../globalStore";
import { splConstants } from "@malloc-protocol/spl";
import { NonSpecificAction } from "@malloc-protocol/sdk";
import { SOURCE_NAME } from "../config/constants";
import { DARK_2, DARK_3 } from "./colors";

const randomPos = (
  xMin: number,
  yMin: number,
  xMax: number,
  yMax: number
): number[] => {
  let x = Math.round(Math.random() * (xMax - xMin) + xMin);
  let y = Math.round(Math.random() * (yMax - yMin) + yMin);
  return [x, y];
};

export interface savedContextState {
  nodes: { [actionId: string]: number[] };
  translate: number[];
  scale: number;
}

export const saveContext = (state: MallocState): savedContextState => {
  const nodes = state.context.nodes;
  let context: savedContextState = {
    nodes: {},
    translate: state.context.translate,
    scale: state.context.scale,
  };
  for (const [node_id, node] of Object.entries(nodes)) {
    context.nodes[node_id] = node.pos;
  }
  return context;
};

export const inferContextFromConstruction = (
  state: MallocState,
  savedContext?: savedContextState
) => {
  const sourceActions = state.construction.source.nextActions;
  const actionDatas = state.construction.actionDatas;

  let nodes = state.context.nodes;
  let sourceNode = nodes[SOURCE_NAME];
  sourceNode.selected = true;

  // start with inferred source nodes
  for (const [action_id, amount] of Object.entries(sourceActions)) {
    let pos = randomPos(-100, -100, 100, 100);
    let node = new Node(action_id, pos);
    nodes[action_id] = node;

    let edge = new Edge(sourceNode, node);
    sourceNode.outEdges[action_id] = edge;
  }

  // CREATE NODE
  for (const action_id of Object.keys(actionDatas)) {
    if (!nodes[action_id]) {
      let pos = randomPos(-100, -100, 100, 100);
      nodes[action_id] = new Node(action_id, pos);
    }
  }

  // CREATE OUT EDGES
  for (const action_id of Object.keys(actionDatas)) {
    let node = nodes[action_id];
    // @ts-ignore
    for (const outMint of actionDatas[action_id].action.nextActions) {
      for (const nextAction_id of Object.keys(outMint)) {
        let node2 = nodes[nextAction_id];
        let edge = new Edge(node, node2);
        node.outEdges[nextAction_id] = edge;
      }
    }
  }

  if (savedContext) {
    state.context.translate = savedContext.translate;
    state.context.scale = savedContext.scale;

    for (let [node_id, node_pos] of Object.entries(savedContext.nodes)) {
      state.context.nodes[node_id].pos = node_pos;
    }
  }

  globalStore.setState({
    ...state,
  });
};

function updateSelection(s: P5State, selected_id: string) {
  s.nodes[s.selectedNodeId].selected = false;
  s.selectedNodeId = selected_id;
  s.nodes[s.selectedNodeId].selected = true;
}

// tests with p5
const GraphVisualizer = () => {
  const state = useStore(globalStore);

  const [dimensions, setDimensions] = React.useState({
    height: 500,
    width: 500,
  });

  let s = state.context;

  let parentRef: Element;
  let clickPos = [0, 0];
  let edgeStart: Node;
  let edgeEnd: Node;
  let canvasDragged = false;
  let nodeDragged = false;
  let edgeDragged = false;

  const setup = (p5: p5Types, canvasParentRef: Element) => {
    parentRef = canvasParentRef;

    // Select a node if mouse over
    const mouseClicked = (e: any) => {
      for (const [id, node] of Object.entries(s.nodes)) {
        if (node.hover) {
          updateSelection(s, id);
        }
      }

      globalStore.setState({
        ...state,
      });
    };

    const makeid = (length: number) => {
      var result = "";
      var characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      var charactersLength = characters.length;
      for (var i = 0; i < length; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        );
      }
      return result;
    };

    // add new node at current position and set selected
    const doubleClicked = (e: any) => {
      let newActionType = Object.values(splConstants.BUILD_ACTION_UIDS)[
        s.selectedActionType
      ];
      let actionIdBase = Object.keys(splConstants.BUILD_ACTION_UIDS)[
        s.selectedActionType
      ];

      let id = actionIdBase + "-" + makeid(5);
      s.nodes[id] = new Node(id, coordMap(p5.mouseX, p5.mouseY));
      updateSelection(s, id);

      let newAction: NonSpecificAction = {
        actionTypeUID: newActionType,
        actionPID: splConstants.PIDS.MAINNET.CPI_ACTION,
        inputs: {},
        nextActions: [],
      };

      state.construction.actionDatas[id] = { action: newAction };

      globalStore.setState({
        ...state,
      });
    };

    const lazyFillNextMints = (
      mintIdx: number,
      currentNextAction: NonSpecificAction["nextActions"]
    ): NonSpecificAction["nextActions"] => {
      if (!currentNextAction[mintIdx]) {
        // Fill in the next action with empty next actions
        for (let i = currentNextAction.length; i <= mintIdx; i++) {
          currentNextAction.push({});
        }
      }
      return currentNextAction;
    };

    // start canvas drag or edge drag
    const mousePressed = (e: any) => {
      for (const [id, node] of Object.entries(s.nodes)) {
        if (node.hover) {
          nodeDragged = true;
          edgeStart = node;
          return;
        }
        if (node.hover2) {
          edgeDragged = true;
          edgeStart = node;
          edgeEnd = new Node("temp", coordMap(p5.mouseX, p5.mouseY));
          edgeEnd.r = 0;
          s.activeEdge = new Edge(edgeStart, edgeEnd);
          return;
        }
      }

      // canvasDragged = true;
      // let [x, y] = coordMap(p5.mouseX, p5.mouseY);
      // clickPos = [s.translate[0] - x, s.translate[1] - y];
    };

    // end canvas drag or edge drag
    const mouseReleased = (e: any) => {
      if (nodeDragged) nodeDragged = false;
      // if (canvasDragged) canvasDragged = false;

      if (edgeDragged) {
        for (const [id, node] of Object.entries(s.nodes)) {
          if (node.hover2 && node != edgeStart) {
            // adds frontend edge
            const newEdge = new Edge(edgeStart, node);
            const newEdgeAmount = 100;
            edgeStart.outEdges[node.id] = newEdge;

            if (edgeStart.id === SOURCE_NAME) {
              state.construction.source.nextActions[node.id] = newEdgeAmount;
            } else {
              const current_node = state.construction.actionDatas[edgeStart.id];
              const outMintIdx = s.selectedOutMintIndex;

              const nextActions = lazyFillNextMints(
                outMintIdx,
                // @ts-ignore
                current_node.action.nextActions
              );
              nextActions[outMintIdx][node.id] = newEdgeAmount;
            }

            globalStore.setState({
              ...state,
            });
          }
        }
        edgeDragged = false;
        s.activeEdge = null;
      }
    };

    // updates dx, dy
    const mouseMoved = (e: any) => {
      // if (canvasDragged) {
      //   s.translate[0] = p5.mouseX + clickPos[0];
      //   s.translate[1] = p5.mouseY + clickPos[1];
      // }

      if (nodeDragged) {
        edgeStart.pos = coordMap(p5.mouseX, p5.mouseY);
      }

      if (edgeDragged) {
        edgeEnd.pos = coordMap(p5.mouseX, p5.mouseY);
      }

      let [x, y] = coordMap(p5.mouseX, p5.mouseY);
      for (const [id, node] of Object.entries(s.nodes)) node.updateHover(x, y);
    };

    // TODO: implement scrolling
    const mouseWheel = (e: any) => {
      s.translate[0] -= e.deltaX;
      s.translate[1] -= e.deltaY;
      // s.scale -= 0.01 * e.deltaY;
    };

    // TODO
    const windowResized = (e: any) => {};
    // s.windowResized = e => {s.resizeCanvas(window.innerWidth, window.innerHeight)}

    p5.createCanvas(canvasParentRef.clientWidth, 500)
      .parent(canvasParentRef)
      .mouseClicked(mouseClicked)
      .doubleClicked(doubleClicked)
      .mousePressed(mousePressed)
      .mouseReleased(mouseReleased)
      .mouseMoved(mouseMoved)
      .mouseWheel(mouseWheel);

    s.translate = [p5.width / 2, p5.height / 2];
  };

  const coordMap = (x: number, y: number) => [
    x / s.scale - s.translate[0],
    y / s.scale - s.translate[1],
  ];

  const applyForces = () => {
    for (const [node_id, node] of Object.entries(s.nodes)) {
      for (const [node2_id, node2] of Object.entries(s.nodes)) {
        if (node_id != node2_id) {
          node.repel(node2);
        }
      }
    }
  };

  const draw = (p5: p5Types) => {
    p5.background(DARK_2);
    p5.noStroke();
    p5.fill(DARK_3);
    const r = 20;
    p5.rect(0, 0, p5.width, p5.height, r, r, r, r);

    p5.push();
    p5.translate(p5.createVector(...s.translate));
    p5.scale(s.scale);

    // draw currently created edge if relevant
    if (s.activeEdge != null) {
      s.activeEdge.draw(p5);
    }

    // draw edges
    for (const [node_id, node] of Object.entries(s.nodes)) {
      for (const [edge_id, edge] of Object.entries(node.outEdges)) {
        edge.draw(p5);
      }
    }

    // draw nodes
    for (const [node_id, node] of Object.entries(s.nodes)) {
      node.draw(p5);
    }

    p5.pop();
    // applyForces();
  };

  // FIX RESIZING LATER
  // useEffect(() => {
  // });

  // useEffect(() => {
  //   function handleResize() {
  //     console.log("resize event");
  //     setDimensions({
  //       height: 500,
  //       width: dimensions.width,
  //     });
  //   }

  //   window.addEventListener("resize", handleResize);
  //   // window.onresize = myHandlerFunction;

  //   // // You can also use:
  //   // // window.addEventListener('resize', myHandlerFunction);
  // }, []);

  return (
    <Sketch
      setup={setup}
      draw={draw}
      windowResized={(p5) => {
        const ref = document.getElementById("graph-container");
        const canvas = document.getElementById("canvas-container");

        // @ts-ignore
        canvas.style.display = "none";
        // @ts-ignore
        p5.resizeCanvas(ref.clientWidth, 500);
        // @ts-ignore
        canvas.style.display = "block";
      }}
    />
  );
};

export default GraphVisualizer;
